// WebSocket transport. One protobuf message per binary frame (no length prefix —
// WS frames are already delimited). Optional per-message secretbox encryption
// once a session key is set. Ported/modernised from RustDesk web client websock.ts.

import { hbb } from "./proto/messages.js";
import { encrypt, decrypt } from "./crypto";

type Decoded = hbb.RendezvousMessage | hbb.Message;

export default class Websock {
  private ws: WebSocket;
  private uri: string;
  private isRendezvous: boolean;
  private status: "" | "open" | "closed" | "error" = "";
  private queue: Decoded[] = [];
  private waiters: Array<(v: Decoded) => void> = [];
  private rejectWaiters: Array<(e: any) => void> = [];
  // [key, sendCounter, recvCounter]; counters incremented BEFORE each use.
  private secret: [Uint8Array, number, number] | undefined;

  latency = 0;

  constructor(uri: string, isRendezvous = true) {
    this.uri = uri;
    this.isRendezvous = isRendezvous;
    this.ws = new WebSocket(uri);
    this.ws.binaryType = "arraybuffer";
    this.ws.onmessage = (e) => this.onFrame(e);
    this.latency = Date.now();
  }

  setSecretKey(key: Uint8Array) {
    this.secret = [key, 0, 0];
  }

  get secured(): boolean {
    return !!this.secret;
  }

  open(timeout = 12000): Promise<Websock> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.status !== "open") reject(new Error("Timeout connecting to " + this.uri));
      }, timeout);
      this.ws.onopen = () => {
        clearTimeout(timer);
        this.latency = Date.now() - this.latency;
        this.status = "open";
        resolve(this);
      };
      this.ws.onclose = () => {
        const wasOpen = this.status === "open";
        this.status = "closed";
        this.failWaiters(new Error(wasOpen ? "Reset by the peer" : "Failed to connect"));
        if (!wasOpen) reject(new Error("Failed to connect to " + this.uri));
      };
      this.ws.onerror = () => {
        if (this.status !== "open") {
          this.status = "error";
          reject(new Error("Failed to connect to " + (this.isRendezvous ? "rendezvous" : "relay") + " server"));
        }
      };
    });
  }

  sendRendezvous(data: hbb.IRendezvousMessage) {
    const bytes: Uint8Array = hbb.RendezvousMessage.encode(hbb.RendezvousMessage.create(data)).finish();
    this.ws.send(bytes as any);
  }

  sendMessage(data: hbb.IMessage) {
    let bytes: Uint8Array = hbb.Message.encode(hbb.Message.create(data)).finish();
    if (this.secret) {
      this.secret[1] += 1;
      bytes = encrypt(bytes, this.secret[1], this.secret[0]);
    }
    this.ws.send(bytes as any);
  }

  /** Await the next decoded message (FIFO). Rejects if the socket closes first. */
  next(timeout = 12000): Promise<Decoded> {
    if (this.queue.length) return Promise.resolve(this.queue.shift()!);
    if (this.status !== "open") return Promise.reject(new Error("socket " + (this.status || "not open")));
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.removeWaiter(resolve, reject);
        reject(new Error("Timeout waiting for message"));
      }, timeout);
      this.waiters.push((v) => {
        clearTimeout(timer);
        resolve(v);
      });
      this.rejectWaiters.push((e) => {
        clearTimeout(timer);
        reject(e);
      });
    });
  }

  close() {
    this.status = "closed";
    if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
      this.ws.close();
    }
    this.ws.onmessage = null;
  }

  private onFrame(e: MessageEvent) {
    if (!(e.data instanceof ArrayBuffer)) return;
    const raw: Uint8Array = new Uint8Array(e.data);

    if (this.secret) {
      const firstAttempt = this.secret[2] === 0; // no frame decrypted yet
      const ctr = this.secret[2] + 1;
      try {
        const plain = decrypt(raw, ctr, this.secret[0]);
        this.secret[2] = ctr; // advance only on success
        const decoded = this.decodeRaw(plain);
        if (decoded) this.deliver(decoded);
        else console.error(`[ws] decode FAIL after decrypt len=${plain.length}`);
        return;
      } catch {
        // The host may not have completed encryption on its side. On the very
        // first frame, if it parses cleanly as plaintext, drop the whole session
        // to unencrypted (send + recv) and continue. (Safe: we're on wss/TLS.)
        if (firstAttempt) {
          const decoded = this.decodeRaw(raw);
          if (decoded && this.hasKnownField(decoded)) {
            console.warn(`[ws] host is unencrypted (len=${raw.length}) — switching session to plaintext`);
            this.secret = undefined;
            this.deliver(decoded);
            return;
          }
        }
        console.error(`[ws] decrypt FAIL len=${raw.length} recvCtr=${ctr}`);
        return; // keep counter so a later good frame still lines up
      }
    }

    // Plaintext session.
    const decoded = this.decodeRaw(raw);
    if (decoded) this.deliver(decoded);
    else console.error(`[ws] decode FAIL len=${raw.length}`);
  }

  private decodeRaw(bytes: Uint8Array): Decoded | undefined {
    try {
      return this.isRendezvous ? hbb.RendezvousMessage.decode(bytes) : hbb.Message.decode(bytes);
    } catch {
      return undefined;
    }
  }

  private hasKnownField(m: Decoded): boolean {
    const active = (m as any).union; // protobufjs oneof virtual: name of set field
    if (typeof active === "string" && active) return true;
    return Object.keys(m).length > 0;
  }

  private deliver(decoded: Decoded) {
    const w = this.waiters.shift();
    if (w) {
      this.rejectWaiters.shift();
      w(decoded);
    } else {
      this.queue.push(decoded);
    }
  }

  private failWaiters(e: any) {
    const rs = this.rejectWaiters.splice(0);
    this.waiters.splice(0);
    for (const r of rs) r(e);
  }

  private removeWaiter(resolve: Function, _reject: Function) {
    // best-effort removal on timeout; index alignment kept by shifting pairs
    const i = this.waiters.findIndex((f) => f === resolve);
    if (i >= 0) {
      this.waiters.splice(i, 1);
      this.rejectWaiters.splice(i, 1);
    }
  }
}
